import React, { useState, useEffect } from 'react';

const CustomModal = ({
    show = false,
    title = '',
    text = '',
    html = null,
    icon = null, // 'success', 'error', 'warning', 'info', or custom
    imageUrl = null,
    showConfirmButton = true,
    showCancelButton = false,
    confirmButtonText = 'OK',
    cancelButtonText = 'Cancel',
    confirmButtonColor = '#3085d6',
    cancelButtonColor = '#d33',
    onConfirm = null,
    onCancel = null,
    onClose = null,
    timer = null,
    timerProgressBar = false,
    allowOutsideClick = true,
    allowEscapeKey = true,
    showClass = {},
    hideClass = {},
    className = '',
    headerColor = null, // Custom header color (default: based on icon)
    errorText = null, // Red text to display (like "Tiebreaker is first to 7 by 2.")
    centerContent = false, // Center body text horizontally and vertically
}) => {
    const [isVisible, setIsVisible] = useState(show);
    const [isClosing, setIsClosing] = useState(false);
    const [timerProgress, setTimerProgress] = useState(100);

    useEffect(() => {
        if (show) {
            setIsVisible(true);
            setIsClosing(false);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        } else {
            handleClose();
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [show]);

    useEffect(() => {
        if (timer && isVisible && !isClosing) {
            if (timerProgressBar) {
                // Progress bar animation
                const interval = 50; // Update every 50ms
                const steps = timer / interval;
                const decrement = 100 / steps;
                
                const progressInterval = setInterval(() => {
                    setTimerProgress((prev) => {
                        const newProgress = prev - decrement;
                        if (newProgress <= 0) {
                            clearInterval(progressInterval);
                            return 0;
                        }
                        return newProgress;
                    });
                }, interval);
                
                const timerId = setTimeout(() => {
                    clearInterval(progressInterval);
                    handleConfirm();
                }, timer);
                
                return () => {
                    clearTimeout(timerId);
                    clearInterval(progressInterval);
                };
            } else {
                const timerId = setTimeout(() => {
                    handleConfirm();
                }, timer);
                return () => clearTimeout(timerId);
            }
        }
    }, [timer, isVisible, isClosing, timerProgressBar]);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsVisible(false);
            if (onClose) onClose();
        }, 300);
    };

    const handleConfirm = () => {
        if (onConfirm) {
            const result = onConfirm();
            if (result !== false) {
                handleClose();
            }
        } else {
            handleClose();
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
        handleClose();
    };

    const handleBackdropClick = (e) => {
        if (allowOutsideClick && e.target === e.currentTarget) {
            handleClose();
        }
    };

    const handleKeyDown = (e) => {
        if (allowEscapeKey && e.key === 'Escape') {
            handleClose();
        }
    };

    useEffect(() => {
        if (isVisible) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isVisible, allowEscapeKey]);

    if (!isVisible) return null;

    const getHeaderColor = () => {
        // Always use #9cb23b color for header
        return headerColor || '#9cb23b';
    };

    const getIconClass = () => {
        switch (icon) {
            case 'success':
                return 'text-success';
            case 'error':
                return 'text-danger';
            case 'warning':
                return 'text-warning';
            case 'info':
                return 'text-info';
            default:
                return '';
        }
    };

    const getIconSymbol = () => {
        switch (icon) {
            case 'success':
                return '✓';
            case 'error':
                return '✕';
            case 'warning':
                return '⚠';
            case 'info':
                return 'ℹ';
            default:
                return null;
        }
    };

    /**
     * Determines the confirm button color based on icon type
     * Validation errors (warning icon) use purple (#b38ec1)
     * All other cases use green (#addd1e)
     * @returns {string} Button color code
     */
    const getConfirmButtonColor = () => {
        // If confirmButtonColor is explicitly provided, use it
        if (confirmButtonColor && confirmButtonColor !== '#3085d6') {
            return confirmButtonColor;
        }
        // If icon is 'warning' (validation error), use purple
        if (icon === 'warning') {
            return '#b38ec1';
        }
        // Default to green for all other cases
        return '#addd1e';
    };

    return (
        <>
            <style>
                {`
                    .modal-dialog.modal-lg {
                        max-width: 1000px !important;
                        width: 96% !important;
                        margin: auto !important;
                    }
                        @media (max-width:1440px) {
                            .modal-dialog.modal-lg {
                               max-width: 800px !important;
                            }
                        }
                `}
            </style>
            {/* Backdrop */}
            <div
                className={`modal-backdrop fade ${isClosing ? '' : 'show'}`}
                onClick={handleBackdropClick}
                style={{ zIndex: 1040 }}
            />

            {/* Modal */}
            <div
                className={`modal fade ${isClosing ? '' : 'show'} ${className}`}
                tabIndex="-1"
                role="dialog"
                style={{
                    display: 'block',
                    zIndex: 1050
                }}
                onClick={handleBackdropClick}
            >
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
                    <div 
                        className={`modal-content${centerContent ? ' custom-modal-3d' : ''}`}
                        style={{ 
                            border: centerContent ? '1px solid #8a8a8a' : 'none',
                            borderTopColor: centerContent ? '#ffffff' : undefined,
                            borderLeftColor: centerContent ? '#f0f0f0' : undefined,
                            borderRightColor: centerContent ? '#6e6e6e' : undefined,
                            borderBottomColor: centerContent ? '#555555' : undefined,
                            borderRadius: '4px', 
                            overflow: 'hidden',
                            boxShadow: centerContent
                                ? '0 10px 28px rgba(0, 0, 0, 0.4), 0 3px 8px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.95), inset 0 -1px 0 rgba(0, 0, 0, 0.08)'
                                : '0 4px 6px rgba(0, 0, 0, 0.1)',
                            backgroundColor: 'white',
                            padding: '6px',
                        }}
                    >
                        {/* Green Header */}
                        {title && (
                            <div 
                                className="modal-header"
                                style={{
                                    backgroundColor: getHeaderColor(),
                                    color: 'white',
                                    padding: '15px 20px',
                                    borderBottom: 'none',
                                    borderRadius: '0',
                                }}
                            >
                                <h5 
                                    className="modal-title mb-0" 
                                    style={{ 
                                        fontSize: '18px', 
                                        fontWeight: 'bold',
                                        color: 'white',
                                        width: '100%',
                                        fontFamily: 'Arial, sans-serif',
                                        borderRadius: '0'
                                    }}
                                >
                                    {title}
                                </h5>
                            </div>
                        )}

                        <div
                            className="modal-body"
                            style={{
                                padding: '20px',
                                position: 'relative',
                                minHeight: centerContent ? '80px' : '100px',
                                ...(centerContent
                                    ? {
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          textAlign: 'center',
                                          borderTop: '1px solid #ffffff',
                                          borderBottom: '1px solid #b0b0b0',
                                          boxShadow:
                                              'inset 0 1px 0 rgba(255, 255, 255, 0.85), inset 0 -2px 3px rgba(0, 0, 0, 0.1)',
                                      }
                                    : {}),
                            }}
                        >
                          
                            {/* Content Container - Centered with left padding for icon */}
                            <div
                                style={
                                    centerContent
                                        ? {
                                              width: '100%',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              textAlign: 'center',
                                          }
                                        : undefined
                                }
                            >
                                {/* Error Text (Red) - if provided */}
                                {errorText && (
                                    <p 
                                        className="mb-2" 
                                        style={{ 
                                            fontSize: '20px', 
                                            color: '#f00',
                                            fontWeight: 'normal',
                                            fontFamily: 'Arial, sans-serif',
                                            marginBottom: text ? '10px' : '0',
                                            textAlign: centerContent ? 'center' : undefined,
                                        }}
                                    >
                                        {errorText}
                                    </p>
                                )}

                                {/* Text or HTML - Centered, Dark Gray */}
                                {html ? (
                                    <div 
                                        dangerouslySetInnerHTML={{ __html: html }} 
                                    />
                                ) : (
                                    text && (
                                        <p 
                                            className="mb-0" 
                                            style={{ 
                                                fontSize: '20px', 
                                                color: '#000',
                                                fontWeight: 'normal',
                                                lineHeight: '1.5',
                                                fontFamily: 'Arial, sans-serif',
                                                textAlign: centerContent ? 'center' : undefined,
                                                margin: centerContent ? '0' : undefined,
                                            }}
                                        >
                                            {text}
                                        </p>
                                    )
                                )}
                            </div>

                            {/* Timer Progress Bar */}
                            {timer && timerProgressBar && (
                                <div className="mt-3 mb-3">
                                    <div
                                        style={{
                                            width: '100%',
                                            height: '4px',
                                            backgroundColor: '#e0e0e0',
                                            borderRadius: '2px',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${timerProgress}%`,
                                                height: '100%',
                                                backgroundColor: getConfirmButtonColor(),
                                                transition: 'width 0.05s linear'
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Gradient Footer */}
                        <div 
                            className="modal-footer"
                            style={{
                                background: '#fff',
                                padding: '10px',
                                borderTop: centerContent ? '1px solid #c8c8c8' : '1px solid #d0d0d0',
                                borderRadius: '0',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '5px',
                                ...(centerContent
                                    ? {
                                          boxShadow:
                                              'inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 -1px 2px rgba(0, 0, 0, 0.06)',
                                      }
                                    : {}),
                            }}
                        >
                            {showCancelButton && (
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={handleCancel}
                                    style={{
                                        backgroundColor: '#addd1e', // purple button
                                        color: '#fff',
                                        minWidth: '70px',
                                        padding: '10px 20px',
                                        borderRadius: '4px',
                                        border: 'none',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase',
                                        fontFamily: 'Arial, sans-serif'
                                    }}
                                >
                                    {cancelButtonText}
                                </button>
                            )}
                             {showConfirmButton && (
                                 <button
                                     type="button"
                                     className="btn"
                                     onClick={handleConfirm}
                                     style={{
                                         backgroundColor: icon === 'success' ? '#addd1e' : (icon === 'warning' || icon === 'error' ? '#b38ec1' : '#addd1e'),
                                         color: '#fff',
                                         minWidth: '70px',
                                         padding: '10px 20px',
                                         borderRadius: '4px',
                                         border: 'none',
                                         fontSize: '14px',
                                         fontWeight: 'bold',
                                         textTransform: 'uppercase',
                                         fontFamily: 'Arial, sans-serif'
                                     }}
                                 >
                                     {confirmButtonText}
                                 </button>
                             )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CustomModal;

